import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Map, { Marker, Layer, Source, MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import { PostResponseSchema, LayerResponseSchema } from '@/api/client';
import { MapMarker } from '@feature/map/components/markers/MapMarker';
import { useMapAnimations } from '../hooks/useMapAnimations';
import { useLayerOutline } from '../hooks/useLayerOutline';
import { LAYER_OUTLINE_CONFIG } from '@/utils/layerOutlineWorker';

import 'mapbox-gl/dist/mapbox-gl.css';

interface ShareMapFullscreenProps {
  post?: PostResponseSchema;
  onMarkerClick?: () => void;
  posts?: PostResponseSchema[];
  currentPostIndex?: number;
  onMarkerClickWithIndex?: (index: number) => void;
  layer?: LayerResponseSchema;
}

const MAPBOX_TOKEN = 'pk.eyJ1Ijoic3Rvcm1ieXRlLWRldiIsImEiOiJjbThxczFyb2owcHpjMmtvZDZ6eG9paTJoIn0.TwaA5xrxn3fwmJaks2c07Q';

export default function ShareMapFullscreen({
  post,
  onMarkerClick,
  posts,
  currentPostIndex = 0,
  onMarkerClickWithIndex,
  layer
}: ShareMapFullscreenProps): React.ReactElement {
  const mapRef = useRef<MapRef>(null);
  const [viewState, setViewState] = useState({
    longitude: 0,
    latitude: 0,
    zoom: 0,
    pitch: 0,
    bearing: 0,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const prevIndexRef = useRef<number>(currentPostIndex);
  const { config, flyToLocation } = useMapAnimations();

  const isMultipleMode = posts && posts.length > 0;
  const effectivePosts = isMultipleMode ? posts : (post ? [post] : []);

  // Process layer geometry
  const layerGeometry = useMemo(() => {
    if (!layer || !layer.geom) {
      return null;
    }

    try {
      const geomData = layer.geom as any;

      if (!geomData.coordinates) {
        console.error('Geometry missing coordinates field');
        return null;
      }

      let validGeometry;
      if (geomData.type) {
        validGeometry = geomData;
      } else {
        const coords = geomData.coordinates;
        const isMultiPolygon =
          Array.isArray(coords) &&
          coords.length > 0 &&
          Array.isArray(coords[0]) &&
          coords[0].length > 0 &&
          Array.isArray(coords[0][0]) &&
          coords[0][0].length > 0 &&
          Array.isArray(coords[0][0][0]);

        validGeometry = {
          type: isMultiPolygon ? 'MultiPolygon' : 'Polygon',
          coordinates: coords,
        };
      }

      if (!validGeometry.type || !validGeometry.coordinates) {
        console.error('Invalid geometry - missing type or coordinates');
        return null;
      }

      return validGeometry;
    } catch (error) {
      console.error('Error processing geometry:', error);
      return null;
    }
  }, [layer]);

  const { outlineData } = useLayerOutline({
    geometry: layerGeometry,
    layerId: layer?.id,
  });

  // Initial globe view setup
  useEffect(() => {
    if (effectivePosts.length === 0) return;

    setViewState({
      longitude: config.globe.center[0],
      latitude: config.globe.center[1],
      zoom: config.globe.zoom,
      pitch: 0,
      bearing: 0,
    });
  }, [config, effectivePosts.length]);

  // Fly to location after initialization
  useEffect(() => {
    if (!isInitialized || !mapRef.current) return;

    if (layer && layerGeometry) {
      // Fit bounds to layer
      const map = mapRef.current.getMap();
      const coordinates = layerGeometry.coordinates;
      const bounds = new mapboxgl.LngLatBounds();

      const addCoordinatesToBounds = (coords: any) => {
        if (Array.isArray(coords[0])) {
          coords.forEach((coord: any) => addCoordinatesToBounds(coord));
        } else {
          bounds.extend(coords as [number, number]);
        }
      };

      addCoordinatesToBounds(coordinates);
      map.fitBounds(bounds, {
        padding: 50,
        duration: 2000,
        pitch: 45,
        bearing: 0
      });
    } else {
      // Fly to post
      const targetPost = isMultipleMode ? posts![0] : post;
      if (targetPost) {
        setTimeout(() => {
          mapRef.current?.flyTo({
            center: [targetPost.longitude, targetPost.latitude],
            zoom: config.initialLoad.zoom,
            pitch: config.initialLoad.pitch,
            bearing: 0,
            duration: config.initialLoad.duration,
          });
        }, 100);
      }
    }
  }, [isInitialized, post, posts, isMultipleMode, config, layer, layerGeometry]);

  // Fly to current post when index changes
  useEffect(() => {
    if (!isInitialized || !isMultipleMode || !posts || posts.length === 0) return;
    if (prevIndexRef.current === currentPostIndex) return;

    prevIndexRef.current = currentPostIndex;
    const currentPost = posts[currentPostIndex];

    if (currentPost && mapRef.current) {
      mapRef.current.flyTo({
        center: [currentPost.longitude, currentPost.latitude],
        zoom: config.transition.zoom,
        pitch: config.transition.pitch,
        duration: config.transition.duration,
      });
    }
  }, [currentPostIndex, posts, isInitialized, isMultipleMode, config]);

  const handleMapLoad = useCallback(() => {
    setIsInitialized(true);
  }, []);

  const handleMarkerClick = useCallback((index: number) => {
    if (isMultipleMode && onMarkerClickWithIndex) {
      onMarkerClickWithIndex(index);
    } else if (onMarkerClick) {
      onMarkerClick();
    }
  }, [isMultipleMode, onMarkerClick, onMarkerClickWithIndex]);

  return (
    <Map
      ref={mapRef}
      {...viewState}
      onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
      onLoad={handleMapLoad}
      mapboxAccessToken={MAPBOX_TOKEN}
      mapStyle="mapbox://styles/mapbox/outdoors-v12"
      style={{ width: '100%', height: '100vh' }}
      projection={{ name: 'globe' }}
      terrain={{ source: 'mapbox-dem', exaggeration: 3.0 }}
      attributionControl={false}
    >
      {/* DEM source for terrain */}
      <Source
        id="mapbox-dem"
        type="raster-dem"
        url="mapbox://mapbox.mapbox-terrain-dem-v1"
        tileSize={512}
        maxzoom={14}
      />

      {/* 3D Buildings */}
      <Layer
        id="3d-buildings"
        source="composite"
        source-layer="building"
        type="fill-extrusion"
        minzoom={8}
        filter={['==', 'extrude', 'true']}
        paint={{
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-opacity': 0.6,
          'fill-extrusion-height': ['get', 'height'],
          'fill-extrusion-base': ['get', 'min_height'],
        }}
      />

      {/* Label opacity adjustments */}
      <Layer
        id="country-label"
        source="composite"
        source-layer="country_label"
        type="symbol"
        paint={{
          'text-opacity': 0.3,
          'icon-opacity': 0.3,
        }}
      />
      <Layer
        id="place-label"
        source="composite"
        source-layer="place_label"
        type="symbol"
        paint={{
          'text-opacity': 0.3,
          'icon-opacity': 0.3,
        }}
      />
      <Layer
        id="road-label"
        source="composite"
        source-layer="road_label"
        type="symbol"
        minzoom={8}
        paint={{
          'text-opacity': 0.3,
          'icon-opacity': 0.3,
        }}
      />
      <Layer
        id="poi-label"
        source="composite"
        source-layer="poi_label"
        type="symbol"
        paint={{
          'text-opacity': 0.3,
          'icon-opacity': 0,
        }}
      />

      {/* Layer interior fill */}
      {layerGeometry && (
        <Source
          id="layer-interior"
          type="geojson"
          data={{
            type: 'Feature',
            properties: {
              layerId: layer?.id?.toString() || 'unknown',
              layerTitle: layer?.title || 'Unknown Layer',
            },
            geometry: layerGeometry,
          }}
        >
          <Layer
            id="layer-interior-fill"
            type="fill-extrusion"
            minzoom={0}
            maxzoom={Math.max(
              10,
              Math.min(
                18,
                12 + Math.log10(1 / (outlineData?.scaling?.heightScale || 0.1)) * 2,
              ),
            )}
            paint={{
              'fill-extrusion-color': LAYER_OUTLINE_CONFIG.interiorColor,
              'fill-extrusion-opacity': LAYER_OUTLINE_CONFIG.interiorOpacity,
              'fill-extrusion-height': LAYER_OUTLINE_CONFIG.interiorHeight * (outlineData?.scaling?.interiorHeightScale || 1),
              'fill-extrusion-base': 0,
            }}
          />
        </Source>
      )}

      {/* Layer outline */}
      {outlineData?.outlineGeometry && (
        <Source
          id="layer-outline"
          type="geojson"
          data={{
            type: 'Feature',
            properties: {
              layerId: layer?.id?.toString() || 'unknown',
              layerTitle: layer?.title || 'Unknown Layer',
            },
            geometry: outlineData.outlineGeometry,
          }}
        >
          <Layer
            id="layer-outline-fill"
            type="fill-extrusion"
            minzoom={0}
            maxzoom={14}
            paint={{
              'fill-extrusion-color': LAYER_OUTLINE_CONFIG.wallColor,
              'fill-extrusion-opacity': LAYER_OUTLINE_CONFIG.wallOpacity,
              'fill-extrusion-height': LAYER_OUTLINE_CONFIG.wallHeight * (outlineData.scaling?.heightScale || 1),
              'fill-extrusion-base': 0,
            }}
          />
        </Source>
      )}

      {/* Markers */}
      {effectivePosts.map((postData, index) => (
        <Marker
          key={postData.id}
          longitude={postData.longitude}
          latitude={postData.latitude}
          anchor="bottom"
        >
          <div
            onClick={() => handleMarkerClick(index)}
            style={{
              cursor: 'pointer',
              transform: index === currentPostIndex ? 'scale(1.15)' : 'scale(1)',
              transition: 'transform 0.2s ease',
            }}
            className={index === currentPostIndex ? 'marker-highlighted' : ''}
          >
            <MapMarker post={postData} />
          </div>
        </Marker>
      ))}

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1.15); }
            50% { transform: scale(1.25); }
            100% { transform: scale(1.15); }
          }
          .marker-highlighted {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
    </Map>
  );
}
